import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  changePassword,
  resetAuthStatuses,
} from "../../store/slices/authSlice";
import "./UserProfileModal.css";

// startOnPassword: open straight on the change-password form (old /change-password links)
const UserProfileModal = ({ isOpen, onClose, startOnPassword = false }) => {
  const dispatch = useDispatch();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // 🔍 Fetching all profile data and API lifecycle statuses from your slice
  const {
    user,
    companyName,
    branchName,
    role,
    departmentName,
    passwordStatus, // loading, succeeded, failed
    error,
  } = useSelector((state) => state.auth);

  // Local UI form states
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState("");

  // Opened from an old /change-password link: go straight to the form
  useEffect(() => {
    if (isOpen && startOnPassword) setIsChangingPassword(true);
  }, [isOpen, startOnPassword]);

  // Clean form and backend error banners when opening/closing or switching panels
  useEffect(() => {
    dispatch(resetAuthStatuses());
    setLocalError("");
  }, [isOpen, isChangingPassword, dispatch]);

  // Handle successful password change side effects
  useEffect(() => {
    if (passwordStatus === "succeeded") {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      const timer = setTimeout(() => {
        setIsChangingPassword(false);
        dispatch(resetAuthStatuses());
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [passwordStatus, dispatch]);

  if (!isOpen) return null;

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setLocalError("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setLocalError("All fields are required.");
      return;
    }

    if (newPassword.length < 6) {
      setLocalError("New password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setLocalError("Passwords do not match.");
      return;
    }

    dispatch(
      changePassword({
        oldPassword: currentPassword,
        newPassword,
      }),
    );
  };

  const handleBackToProfile = () => {
    setIsChangingPassword(false);
  };

  return (
    <div className="modal fade show d-block upm-backdrop" tabIndex={-1}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content glass-modal-card border-0 p-2 p-sm-4">
          {/* HEADER SECTION */}
          <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-start">
            <div className="d-flex align-items-center gap-3">
              <div
                className={`d-flex align-items-center justify-content-center text-white fw-bold upm-icon-badge ${isChangingPassword ? "is-muted" : ""}`}
              >
                <i
                  className={`bi ${isChangingPassword ? "bi-shield-lock-fill" : "bi-person-badge-fill"} fs-140`}
                ></i>
              </div>
              <div>
                <h3 className="fw-bolder m-0 ls-n10 text-navy">
                  {isChangingPassword ? (
                    <>
                      Change <span className="text-slate-500">Password</span>
                    </>
                  ) : (
                    <>
                      User <span className="text-blue-500">Profile</span>
                    </>
                  )}
                </h3>
              </div>
            </div>

            <button
              type="button"
              className="btn rounded-circle d-flex align-items-center justify-content-center close-btn-hover transition-all upm-close"
              onClick={onClose}
              disabled={passwordStatus === "loading"}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          {/* VIEW SWITCHER PANELS */}
          {!isChangingPassword ? (
            /* =======================================
               PANEL A: PROFILE DETAILS VIEW
               ======================================= */
            <>
              <div className="modal-body py-4">
                <div className="text-center p-3 mb-4 rounded-4 upm-dashed">
                  <h5 className="fw-bold m-0 text-navy">
                    {user?.name || "User Account"}
                  </h5>
                  <p className="text-muted small m-0">
                    {user?.email || "No Email Bound"}
                  </p>
                </div>

                <div className="row g-3">
                  <div className="col-12">
                    <div className="info-pill p-3 d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-building text-primary fs-5"></i>
                        <span className="small text-secondary fw-semibold">
                          Company
                        </span>
                      </div>
                      <span className="fw-bold small text-dark">
                        {companyName || "N/A"}
                      </span>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="info-pill p-3 d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-geo-alt-fill text-danger fs-5"></i>
                        <span className="small text-secondary fw-semibold">
                          Branch Location
                        </span>
                      </div>
                      <span className="fw-bold small text-dark">
                        {branchName || "N/A"}
                      </span>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="info-pill p-3 d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-diagram-3-fill text-success fs-5"></i>
                        <span className="small text-secondary fw-semibold">
                          Department
                        </span>
                      </div>
                      <span className="fw-bold small text-dark">
                        {departmentName || "N/A"}
                      </span>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="info-pill p-3 d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-shield-lock-fill text-warning fs-5"></i>
                        <span className="small text-secondary fw-semibold">
                          Access Level
                        </span>
                      </div>
                      <span className="badge px-3 py-2 rounded-pill text-xs fw-bold upm-role-badge">
                        {role || "Employee"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer border-0 pt-0 d-flex flex-column gap-2">
                <button
                  type="button"
                  className="btn w-100 fw-bold shadow-sm border-0 transition-all upm-btn bg-slate-100 text-slate-700"
                  onClick={() => setIsChangingPassword(true)}
                >
                  <i className="bi bi-key me-2"></i>Change System Password
                </button>

                <button
                  type="button"
                  className="btn w-100 fw-bold shadow-sm border-0 text-white transition-all upm-btn bg-navy"
                  onClick={onClose}
                >
                  Close Profile View
                </button>
              </div>
            </>
          ) : (
            /* =======================================
               PANEL B: PASSWORD ALTERATION FORM
               ======================================= */
            <form onSubmit={handlePasswordSubmit}>
              <div className="modal-body py-4">
                {/* LOCAL ERROR BANNER */}
                {localError && (
                  <div className="alert alert-danger py-2 border-0 small mb-3 d-flex align-items-center rounded-3">
                    <i className="bi bi-exclamation-circle-fill me-2"></i>
                    {localError}
                  </div>
                )}

                {/* REDUX REJECTION BANNER */}
                {error && !localError && (
                  <div className="alert alert-danger py-2 border-0 small mb-3 d-flex align-items-center rounded-3">
                    <i className="bi bi-exclamation-circle-fill me-2"></i>
                    {error}
                  </div>
                )}

                {/* REDUX SUCCESS CONFIRMATION */}
                {passwordStatus === "succeeded" && (
                  <div className="alert alert-success py-2 border-0 small mb-3 d-flex align-items-center rounded-3">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Password changed successfully! Returning to profile...
                  </div>
                )}

                {/* CURRENT PASSWORD */}
                <div className="mb-3">
                  <label className="form-label fw-bold small text-secondary">
                    Current Password
                  </label>

                  <div className="position-relative">
                    <input
                      type={showCurrent ? "text" : "password"}
                      className="form-control form-control-lg profile-input pe-5 fs-090 rounded-10"
                      placeholder="••••••••"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      disabled={
                        passwordStatus === "loading" ||
                        passwordStatus === "succeeded"
                      }
                      required
                    />
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-secondary"
                      onClick={() => setShowCurrent(!showCurrent)}
                      disabled={
                        passwordStatus === "loading" ||
                        passwordStatus === "succeeded"
                      }
                    >
                      <i
                        className={
                          showCurrent ? "bi bi-eye-slash" : "bi bi-eye"
                        }
                      ></i>
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold small text-secondary">
                    New Password
                  </label>

                  <div className="position-relative">
                    <input
                      type={showNew ? "text" : "password"}
                      className="form-control form-control-lg profile-input pe-5 fs-090 rounded-10"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={
                        passwordStatus === "loading" ||
                        passwordStatus === "succeeded"
                      }
                      required
                    />
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-secondary"
                      onClick={() => setShowNew(!showNew)}
                      disabled={
                        passwordStatus === "loading" ||
                        passwordStatus === "succeeded"
                      }
                    >
                      <i
                        className={showNew ? "bi bi-eye-slash" : "bi bi-eye"}
                      ></i>
                    </button>
                  </div>
                </div>

                <div className="mb-1">
                  <label className="form-label fw-bold small text-secondary">
                    Confirm New Password
                  </label>

                  <div className="position-relative">
                    <input
                      type={showConfirm ? "text" : "password"}
                      className="form-control form-control-lg profile-input pe-5 fs-090 rounded-10"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={
                        passwordStatus === "loading" ||
                        passwordStatus === "succeeded"
                      }
                      required
                    />
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-secondary"
                      onClick={() => setShowConfirm(!showConfirm)}
                      disabled={
                        passwordStatus === "loading" ||
                        passwordStatus === "succeeded"
                      }
                    >
                      <i
                        className={
                          showConfirm ? "bi bi-eye-slash" : "bi bi-eye"
                        }
                      ></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-footer border-0 pt-0 d-flex gap-2">
                <button
                  type="button"
                  className="btn flex-grow-1 fw-bold border-0 transition-all upm-btn upm-btn--muted"
                  onClick={handleBackToProfile}
                  disabled={
                    passwordStatus === "loading" ||
                    passwordStatus === "succeeded"
                  }
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-grow-1 fw-bold shadow-sm border-0 transition-all d-flex align-items-center justify-content-center upm-btn bg-blue-500"
                  disabled={
                    passwordStatus === "loading" ||
                    passwordStatus === "succeeded"
                  }
                >
                  {passwordStatus === "loading" ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    "Save Password"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;
